import React, { useEffect, useRef, useState } from 'react';
import * as LC from '../../face/engine/liveness-core';
import { FaceEngine, browserEnv } from '../../face/engine/faceengine';
import { loadFaceApi, MODEL_URL } from '../../face/faceApi';
import { hr } from '../../face/hrApi';

/* Guided enrolment: five samples (straight, slightly left, slightly right, smile, straight again).
   Each is captured automatically once the pose and image quality have held for a few frames. */
const POSES = [
  { key: 'front', label: 'Look straight at the camera', short: 'Straight', ok: (m) => Math.abs(m.yaw) <= 0.18 && (m.smile ?? 0) < 0.5, yawLimit: 0.25, why: (m) => (Math.abs(m.yaw) > 0.18 ? 'Face the camera' : 'Relax your face') },
  { key: 'left', label: 'Turn slightly to your left', short: 'Slightly left', ok: (m) => m.yaw >= 0.28 && m.yaw <= 0.6, yawLimit: 0.7, why: (m) => (m.yaw < 0.28 ? 'A little more to your left' : 'Not that far — a little back') },
  { key: 'right', label: 'Turn slightly to your right', short: 'Slightly right', ok: (m) => m.yaw <= -0.28 && m.yaw >= -0.6, yawLimit: 0.7, why: (m) => (m.yaw > -0.28 ? 'A little more to your right' : 'Not that far — a little back') },
  { key: 'smile', label: 'Smile naturally', short: 'Smiling', ok: (m) => Math.abs(m.yaw) <= 0.2 && (m.smile ?? 0) >= 0.6, yawLimit: 0.25, why: (m) => (Math.abs(m.yaw) > 0.2 ? 'Face the camera' : 'Smile') },
  { key: 'front', label: 'Straight again, neutral face', short: 'Straight again', ok: (m) => Math.abs(m.yaw) <= 0.18 && (m.smile ?? 0) < 0.5, yawLimit: 0.25, why: (m) => (Math.abs(m.yaw) > 0.18 ? 'Face the camera' : 'Relax your face') },
];
const HOLD_FRAMES = 4;
const euclid = (a, b) => { let s = 0; for (let i = 0; i < a.length; i++) { const d = a[i] - b[i]; s += d * d; } return Math.sqrt(s); };

export default function EnrolModal({ employee, onClose }) {
  const videoRef = useRef(null);
  const overlayRef = useRef(null);
  const E = useRef({ samples: [], hold: 0, mode: 'capture', running: false, engine: null, settings: null, stream: null, test: null });

  const [samples, setSamples] = useState([]);
  const [say, setSay] = useState('Starting camera…');
  const [tone, setTone] = useState('');
  const [hold, setHold] = useState(0);
  const [saved, setSaved] = useState(false);
  const [notice, setNotice] = useState(null);
  const [dup, setDup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testOut, setTestOut] = useState(null);

  useEffect(() => {
    const st = E.current;
    let dead = false;

    const thumbCanvas = document.createElement('canvas');
    const thumb = (m) => {
      const v = videoRef.current, pad = m.box.w * 0.25;
      const x = Math.max(0, m.box.x - pad), y = Math.max(0, m.box.y - pad);
      const w = Math.min(v.videoWidth - x, m.box.w + pad * 2), h = Math.min(v.videoHeight - y, m.box.h + pad * 2);
      thumbCanvas.width = 96; thumbCanvas.height = Math.round((96 * h) / w);
      thumbCanvas.getContext('2d').drawImage(v, x, y, w, h, 0, 0, thumbCanvas.width, thumbCanvas.height);
      return thumbCanvas.toDataURL('image/jpeg', 0.7);
    };
    const drawOverlay = (m) => {
      const c = overlayRef.current, v = videoRef.current;
      if (!c) return;
      const cw = c.clientWidth, ch = c.clientHeight;
      if (c.width !== cw || c.height !== ch) { c.width = cw; c.height = ch; }
      const g = c.getContext('2d'); g.clearRect(0, 0, cw, ch);
      if (!m) return;
      const s = Math.max(cw / v.videoWidth, ch / v.videoHeight), ox = (cw - v.videoWidth * s) / 2, oy = (ch - v.videoHeight * s) / 2;
      g.fillStyle = 'rgba(255,120,120,.85)';
      for (const p of m.landmarks) { g.beginPath(); g.arc(p.x * s + ox, p.y * s + oy, 1.6, 0, 6.3); g.fill(); }
    };
    const feedback = (t, s) => { setTone(t); setSay(s); };
    const qualityScore = (m) => Math.round(Math.min(100, m.score * 55 + Math.min(1, m.sharp / Math.max(1, st.settings.minSharpness * 5)) * 45));
    const tick = () => new Promise((r) => requestAnimationFrame(r));

    async function captureStep() {
      const idx = st.samples.length;
      if (idx >= POSES.length) { st.mode = 'idle'; return; }
      const pose = POSES[idx], t = st.settings;
      const want = (m, vs) => st.hold + 1 >= HOLD_FRAMES && pose.ok(m) && LC.probeIssues({ score: m.score, w: m.box.w, videoW: vs.w, sharp: m.sharp, bright: m.bright, yaw: m.yaw }, t, { frontalYaw: pose.yawLimit }).length === 0;
      const r = await st.engine.analyze(videoRef.current, { search: true, expressions: true, quality: true, descriptor: want });
      drawOverlay(r.main);
      const m = r.main;
      setHold(Math.min(100, (st.hold / HOLD_FRAMES) * 100));
      if (!m) { st.hold = 0; feedback('', 'No face found — move into the frame'); return; }
      if (r.count > 1) { st.hold = 0; feedback('bad', 'Only one person in front of the camera'); return; }
      const issues = LC.probeIssues({ score: m.score, w: m.box.w, videoW: r.video.w, sharp: m.sharp, bright: m.bright, yaw: 0 }, t);
      if (issues.length) { st.hold = 0; feedback('bad', issues[0][0].toUpperCase() + issues[0].slice(1)); return; }
      if (!pose.ok(m)) { st.hold = 0; feedback('', `${pose.label} — ${pose.why(m)}`); return; }
      st.hold++;
      feedback('ready', 'Hold still…');
      if (st.hold >= HOLD_FRAMES && m.descriptor) {
        const sample = { pose: pose.key, descriptor: m.descriptor.map((x) => Number(x.toFixed(6))), quality: qualityScore(m), thumb: thumb(m) };
        const first = st.samples[0];
        if (first && euclid(first.descriptor, sample.descriptor) > 0.6) { st.hold = 0; feedback('bad', 'This does not look like the first sample — only one person please'); return; }
        st.samples = [...st.samples, sample]; st.hold = 0;
        setSamples(st.samples);
        if (st.samples.length === POSES.length) { feedback('ready', 'All samples captured. Save the enrolment.'); setHold(100); }
      }
    }

    async function testStep() {
      const T = st.test, t = st.settings, now = performance.now() - T.started;
      const r = await st.engine.analyze(videoRef.current, { search: true, quality: true, descriptor: (m, vs) => now - T.last > 250 && LC.probeIssues({ score: m.score, w: m.box.w, videoW: vs.w, sharp: m.sharp, bright: m.bright, yaw: m.yaw }, t).length === 0 });
      drawOverlay(r.main);
      if (r.main && r.count === 1 && r.main.descriptor) { T.probes.push({ descriptor: r.main.descriptor.map((x) => Number(x.toFixed(6))) }); T.last = now; }
      if (T.probes.length >= 5 || now > 9000) {
        st.mode = 'idle';
        if (!T.probes.length) { setTestOut({ bad: true, t: 'No clear frontal face seen. Try again.' }); st.mode = 'testready'; return; }
        try {
          const res = await hr('/enroll/test', { method: 'POST', body: { probes: T.probes } });
          const who = res.employee ? `${res.employee.name} (${res.employee.code})` : 'nobody';
          const good = res.status === 'matched' && res.employee && res.employee.id === employee.id;
          setTestOut(good
            ? { ok: true, t: `Recognised as ${who}. Match strength ${Math.round(res.strength)}/100.` }
            : { bad: true, t: res.status === 'matched' ? `Recognised as ${who} — the wrong person. Retake the samples in better light.` : `Not recognised (${res.reason || res.status}). Retake the samples in better light.` });
        } catch (e) { setTestOut({ bad: true, t: e.message }); }
        st.mode = 'testready';
      }
    }

    (async () => {
      try {
        const [fa, cfg] = await Promise.all([loadFaceApi(), hr('/settings')]);
        st.settings = cfg.settings;
        st.engine = new FaceEngine(fa, browserEnv());
        await fa.tf.ready(); await st.engine.load(MODEL_URL);
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) throw new Error('The camera needs HTTPS.');
        st.stream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' }, audio: false });
        if (dead) { st.stream.getTracks().forEach((x) => x.stop()); return; }
        const v = videoRef.current; v.srcObject = st.stream;
        await new Promise((res) => { if (v.readyState >= 2 && v.videoWidth) res(); else v.addEventListener('loadeddata', res, { once: true }); });
        await v.play().catch(() => {});
      } catch (e) { setTone('bad'); setSay(e.name === 'NotAllowedError' ? 'Camera blocked — allow it for this site and reopen' : (e.message || 'Camera error')); return; }
      st.running = true; feedback('', POSES[0].label);
      while (st.running && !dead) {
        try {
          if (st.mode === 'capture') await captureStep();
          else if (st.mode === 'test') await testStep();
          else await new Promise((r) => setTimeout(r, 200));
        } catch (e) { console.error(e); await new Promise((r) => setTimeout(r, 400)); }
        await tick();
      }
    })();

    return () => { dead = true; st.running = false; try { if (st.stream) st.stream.getTracks().forEach((x) => x.stop()); } catch (_) { /* gone */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retake = (i) => { const st = E.current; st.samples = st.samples.slice(0, i); st.hold = 0; st.mode = 'capture'; setSamples(st.samples); setSaved(false); setNotice(null); setDup(null); setTestOut(null); };
  const restart = () => retake(0);

  const save = async (force) => {
    const st = E.current; setBusy(true); setNotice(null);
    try {
      const templates = st.samples.map((s) => ({ descriptor: s.descriptor, pose: s.pose, quality: s.quality }));
      const r = await hr(`/enroll/${employee.id}`, { method: 'POST', body: { templates, force: !!force } });
      setSaved(true); setDup(null); st.mode = 'testready';
      setNotice({ ok: true, t: `Saved ${r.saved} samples for ${employee.full_name}. You can now test recognition below.` });
    } catch (e) {
      if (e.status === 409 && e.body.duplicate_of) setDup(e.body);
      setNotice({ bad: true, t: e.message });
    } finally { setBusy(false); }
  };

  const runTest = () => {
    const st = E.current;
    st.test = { probes: [], started: performance.now(), last: -1e9 }; st.mode = 'test'; setTestOut({ t: 'Look at the camera…' });
  };

  const ready = samples.length === POSES.length;
  return (
    <div className="fc-modal" onClick={(e) => e.stopPropagation()}>
      <div className="fc-modal-box fc-enrol">
        <div className="fc-enrol-head">
          <div><h3>{samples.length ? 'Enrol' : 'Enrol'} {employee.full_name}</h3><div className="fc-muted fc-small">{employee.code}{employee.department ? ` · ${employee.department}` : ''}{employee.samples ? ` · ${employee.samples} samples on file (saving replaces them)` : ''}</div></div>
          <button className="fc-btn fc-btn-ghost fc-btn-sm" onClick={onClose}>Close</button>
        </div>
        <div className="fc-enrol-body">
          <div className={`fc-cam ${tone}`}>
            <video ref={videoRef} playsInline muted autoPlay />
            <canvas ref={overlayRef} />
            <div className="fc-cam-say">{say}</div>
            <div className="fc-cam-hold"><span style={{ width: `${hold}%` }} /></div>
          </div>
          <div className="fc-poses">
            {POSES.map((p, i) => {
              const s = samples[i];
              const cur = !s && i === samples.length;
              return (
                <div className={`fc-pose ${s ? 'done' : cur ? 'cur' : ''}`} key={i}>
                  {s ? <img alt="" src={s.thumb} /> : <div className="fc-pose-ph" />}
                  <div className="fc-pose-t"><b>{p.short}</b><div className="fc-muted fc-small">{s ? `quality ${s.quality}` : cur ? 'capturing…' : 'waiting'}</div></div>
                  {s && <button className="fc-link" onClick={() => retake(i)}>Retake</button>}
                </div>
              );
            })}
            <div className="fc-enrol-actions">
              <button className="fc-btn fc-btn-red" disabled={!ready || busy || saved} onClick={() => save(false)}>{busy ? 'Saving…' : saved ? 'Saved' : 'Save enrolment'}</button>
              <button className="fc-btn fc-btn-ghost" onClick={restart}>Start over</button>
            </div>
            {notice && <div className={`fc-callout ${notice.bad ? 'fc-bad' : 'fc-ok'}`}>{notice.t}
              {dup && <div style={{ marginTop: 8 }}><button className="fc-btn fc-btn-sm fc-btn-ghost" onClick={() => { if (window.confirm('Two employees with nearly identical faces can be confused by the kiosk. Save anyway only if you are sure (for example identical twins).')) save(true); }}>This is a different person — save anyway</button></div>}
            </div>}
            {saved && (
              <div className="fc-test">
                <button className="fc-btn fc-btn-ghost" onClick={runTest}>Test recognition</button>
                {testOut && <div className={`fc-callout ${testOut.bad ? 'fc-bad' : testOut.ok ? 'fc-ok' : ''}`}>{testOut.t}</div>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
